import { useEffect, useState } from "react";
import { WITH_COOKIE } from "../tokenStorage";
import { AdminNav } from "./AdminPage";

/** What app/services/campaigns.py's report() says about one campaign. */
interface CampaignReport {
  status: string;
  batches_sent: number;
  next_batch_at: string | null;
  total_recipients: number;
  pending: number;
  sending: number;
  sent: number;
  failed: number;
  skipped: number;
  skipped_by_reason: Record<string, number>;
  unsubscribed: number;
  bounced: number;
  complained: number;
}

interface Campaign {
  id: string;
  name: string;
  subject: string;
  body_text: string;
  body_html: string | null;
  from: string;
  reply_to: string | null;
  sources: string[];
  start_at: string;
  batch_size: number;
  batch_interval_minutes: number;
  dry_run: boolean;
  status: "draft" | "running" | "paused" | "completed" | "cancelled";
  report: CampaignReport;
}

interface Overview {
  sending_enabled: boolean;
  sending_disabled_reason: string | null;
  mailer: string;
  owner_email: string;
  postal_address_set: boolean;
  postal_address: string | null;
  sources: Record<string, string>;
  max_batch_size: number;
  campaigns: Campaign[];
}

interface DryRun {
  campaign_id: string;
  raw_counts: Record<string, number>;
  duplicates_removed: number;
  skipped_counts: Record<string, number>;
  recipient_count: number;
  batches: number;
  recipients: string[];
  skipped: { email: string; reason: string }[];
}

interface Suppression {
  email: string;
  reason: string;
  source: string | null;
  created_at: string;
}

interface AdminCampaignsPageProps {
  apiUrl: string;
  token: string;
  tokenResolved: boolean;
}

// The form's fields, as typed. start_at is a datetime-local value, in the
// browser's own timezone; it's turned into an ISO time with its offset only
// when saved, which is what the backend insists on.
interface Draft {
  id: string;
  name: string;
  subject: string;
  body_text: string;
  body_html: string;
  from: string;
  reply_to: string;
  sources: string[];
  start_at: string;
  batch_size: string;
  batch_interval_minutes: string;
  dry_run: boolean;
}

const DEFAULT_SOURCES = ["design_email_opt_in", "accounts"];

function emptyDraft(): Draft {
  return {
    id: "",
    name: "",
    subject: "",
    body_text: "",
    body_html: "",
    from: "",
    reply_to: "",
    sources: DEFAULT_SOURCES,
    start_at: "",
    batch_size: "50",
    batch_interval_minutes: "10",
    dry_run: true,
  };
}

// A datetime-local value for an ISO time: the same moment, in local time.
function localInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function draftOf(c: Campaign): Draft {
  return {
    id: c.id,
    name: c.name,
    subject: c.subject,
    body_text: c.body_text,
    body_html: c.body_html ?? "",
    from: c.from,
    reply_to: c.reply_to ?? "",
    sources: c.sources,
    start_at: localInput(c.start_at),
    batch_size: String(c.batch_size),
    batch_interval_minutes: String(c.batch_interval_minutes),
    dry_run: c.dry_run,
  };
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

async function failure(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body.detail === "string") return body.detail;
  } catch {
    // Not JSON: fall through to the status.
  }
  return `HTTP ${res.status}`;
}

const STATUS_LABEL: Record<Campaign["status"], string> = {
  draft: "Draft",
  running: "Running",
  paused: "Paused",
  completed: "Completed",
  cancelled: "Cancelled",
};

/**
 * Email campaigns (EMB-37): what scripts/email_campaign.py does, from the
 * browser. Define a draft, dry-run it, test-send it to the owner, start it,
 * pause, resume or cancel it, and keep the suppression list.
 *
 * Unlike the other admin pages, this one is the admin's alone on dev too:
 * the API answers 404 to anyone else everywhere, since it shows recipient
 * lists (app/routers/admin_campaigns.py).
 */
export function AdminCampaignsPage({ apiUrl, token, tokenResolved }: AdminCampaignsPageProps) {
  const [access, setAccess] = useState<"checking" | "denied" | "granted">("checking");
  const [overview, setOverview] = useState<Overview | null>(null);
  const [suppressions, setSuppressions] = useState<Suppression[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The campaign form: null when closed. `editing` is the id of the draft
  // being changed, or null for a new one.
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState<DryRun | null>(null);
  // The campaign whose start is being confirmed, and the id typed for it.
  const [starting, setStarting] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState("");
  const [suppressInput, setSuppressInput] = useState("");

  const post = (path: string, payload: object = {}) =>
    fetch(`${apiUrl}/api/admin${path}`, {
      ...WITH_COOKIE,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token || null, ...payload }),
    });

  const load = async () => {
    const [list, supp] = await Promise.all([post("/campaigns/list"), post("/suppressions/list")]);
    if (list.ok) setOverview(await list.json());
    else setError(`Could not load campaigns: ${await failure(list)}`);
    if (supp.ok) setSuppressions(await supp.json());
  };

  useEffect(() => {
    if (!tokenResolved) return;
    (async () => {
      try {
        const res = await post("/campaigns/list");
        if (!res.ok) {
          setAccess("denied");
          return;
        }
        setOverview(await res.json());
        setAccess("granted");
        const supp = await post("/suppressions/list");
        if (supp.ok) setSuppressions(await supp.json());
      } catch (err) {
        setError(`Could not reach the server: ${err}`);
        setAccess("denied");
      }
    })();
  }, [tokenResolved, token]);

  // Runs one admin action: clears the messages, shows a notice on success
  // and the API's reason on failure, and reloads everything after.
  const act = async (run: () => Promise<Response>, onOk: (body: any) => string | null) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await run();
      if (!res.ok) {
        setError(await failure(res));
        return false;
      }
      setNotice(onOk(await res.json()));
      await load();
      return true;
    } catch (err) {
      setError(`Could not reach the server: ${err}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openForm = (campaign?: Campaign) => {
    setDraft(campaign ? draftOf(campaign) : emptyDraft());
    setEditing(campaign?.id ?? null);
    setError(null);
    setNotice(null);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    const start = new Date(draft.start_at);
    if (!draft.start_at || Number.isNaN(start.getTime())) {
      setError("Pick a start time.");
      return;
    }
    const definition: Record<string, unknown> = {
      id: draft.id.trim(),
      name: draft.name.trim() || undefined,
      subject: draft.subject,
      body_text: draft.body_text,
      body_html: draft.body_html.trim() || undefined,
      from: draft.from.trim() || undefined,
      reply_to: draft.reply_to.trim() || undefined,
      sources: draft.sources,
      start_at: start.toISOString(),
      batch_size: Number(draft.batch_size),
      batch_interval_minutes: Number(draft.batch_interval_minutes),
      dry_run: draft.dry_run,
    };
    const ok = await act(
      () => post("/campaigns", { definition }),
      (body) => `${body.created ? "Created" : "Saved"} ${body.campaign.id}.`,
    );
    if (ok) {
      setDraft(null);
      setEditing(null);
    }
  };

  const runDryRun = async (id: string) => {
    setDryRun(null);
    await act(
      () => post(`/campaigns/${id}/dry-run`),
      (body) => {
        setDryRun(body);
        return null;
      },
    );
  };

  const testSend = (id: string) =>
    act(
      () => post(`/campaigns/${id}/test-send`),
      (body) => `Test send of ${id} handed to ${body.mailer} for ${body.to}.`,
    );

  const start = async (id: string) => {
    const ok = await act(
      () => post(`/campaigns/${id}/start`, { confirm: confirmId }),
      (body) => `Started ${id}: ${body.recipients} recipients snapshotted.`,
    );
    if (ok) {
      setStarting(null);
      setConfirmId("");
    }
  };

  const setStatus = (id: string, action: "pause" | "resume" | "cancel") => {
    if (action === "cancel" && !window.confirm(`Cancel ${id}? A cancelled campaign can't be started again.`)) return;
    return act(
      () => post(`/campaigns/${id}/${action}`),
      (body) => `${id} is now ${body.status}.`,
    );
  };

  const suppress = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await act(
      () => post("/suppressions", { email: suppressInput }),
      (body) => (body.added ? `Suppressed ${suppressInput.trim()}.` : `${suppressInput.trim()} was already suppressed.`),
    );
    if (ok) setSuppressInput("");
  };

  const unsuppress = (email: string) => {
    if (!window.confirm(`Let campaigns email ${email} again?`)) return;
    return act(
      () => post("/suppressions/remove", { email }),
      () => `Removed ${email} from the suppression list.`,
    );
  };

  if (access === "checking") return <p aria-busy="true">Loading…</p>;

  if (access === "denied") {
    return (
      <article>
        <h2>Admin</h2>
        <p>This page isn't available.</p>
      </article>
    );
  }

  const field = (key: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDraft((d) => (d ? { ...d, [key]: e.target.value } : d));

  return (
    <>
      <AdminNav current="campaigns" />

      {error && (
        <p role="alert" style={{ color: "var(--pico-del-color, #b3261e)" }}>
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}

      {overview && (
        <article>
          <h2>Sending</h2>
          <p>
            {overview.sending_enabled ? (
              <strong>On: started campaigns send their batches on schedule.</strong>
            ) : (
              <>
                <strong>Off</strong>: {overview.sending_disabled_reason}. No batch goes out, whatever a campaign's own
                status.
              </>
            )}
          </p>
          <small>
            The switch is the <code>email_campaigns</code> feature flag in PostHog. Turning it off stops every
            campaign at once.
          </small>
          <ul>
            <li>
              Mailer: <code>{overview.mailer}</code>
            </li>
            <li>Test sends go to {overview.owner_email}</li>
            <li>
              Sender address in the footer:{" "}
              {overview.postal_address_set ? (
                <code>{overview.postal_address}</code>
              ) : (
                <strong>empty. Nothing can be test-sent or started until EMAIL_CAMPAIGN_POSTAL_ADDRESS is set.</strong>
              )}
            </li>
          </ul>
        </article>
      )}

      <article>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
          <h2 style={{ margin: 0 }}>Campaigns</h2>
          {!draft && (
            <button type="button" onClick={() => openForm()} style={{ width: "auto", margin: 0 }}>
              New campaign
            </button>
          )}
        </header>
        {overview?.campaigns.length === 0 && <p>No campaigns yet.</p>}
        {overview?.campaigns.map((c) => (
          <section key={c.id} style={{ borderBottom: "1px solid var(--pico-muted-border-color)", paddingBottom: "1rem" }}>
            <h3 style={{ marginBottom: "0.25rem" }}>
              {c.name} <small>({c.id})</small>
            </h3>
            <p style={{ marginBottom: "0.5rem" }}>
              <mark>{STATUS_LABEL[c.status]}</mark>
              {c.dry_run && " · dry-run mode (can't be started)"} · starts {when(c.start_at)} · {c.batch_size} every{" "}
              {c.batch_interval_minutes} min
              {c.report.next_batch_at && <> · next batch {when(c.report.next_batch_at)}</>}
            </p>
            <p style={{ marginBottom: "0.5rem" }}>
              <strong>Subject:</strong> {c.subject}
            </p>
            {c.status !== "draft" && (
              <p style={{ marginBottom: "0.5rem" }}>
                {c.report.total_recipients} recipients: {c.report.sent} sent, {c.report.pending + c.report.sending}{" "}
                pending, {c.report.failed} failed, {c.report.skipped} skipped
                {Object.keys(c.report.skipped_by_reason).length > 0 &&
                  ` (${Object.entries(c.report.skipped_by_reason)
                    .map(([reason, n]) => `${n} ${reason}`)
                    .join(", ")})`}
                . {c.report.unsubscribed} unsubscribed, {c.report.bounced} bounced, {c.report.complained} complained.{" "}
                {c.report.batches_sent} batch(es) sent.
              </p>
            )}
            <div role="group" style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", width: "auto" }}>
              {c.status === "draft" && (
                <button type="button" className="outline" disabled={busy} onClick={() => openForm(c)}>
                  Edit
                </button>
              )}
              <button type="button" className="outline" disabled={busy} onClick={() => runDryRun(c.id)}>
                Dry run
              </button>
              <button type="button" className="outline" disabled={busy} onClick={() => testSend(c.id)}>
                Test send
              </button>
              {c.status === "draft" && !c.dry_run && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setStarting(c.id);
                    setConfirmId("");
                  }}
                >
                  Start…
                </button>
              )}
              {c.status === "running" && (
                <button type="button" className="secondary" disabled={busy} onClick={() => setStatus(c.id, "pause")}>
                  Pause
                </button>
              )}
              {c.status === "paused" && (
                <button type="button" disabled={busy} onClick={() => setStatus(c.id, "resume")}>
                  Resume
                </button>
              )}
              {["draft", "running", "paused"].includes(c.status) && (
                <button type="button" className="secondary outline" disabled={busy} onClick={() => setStatus(c.id, "cancel")}>
                  Cancel
                </button>
              )}
            </div>
            {starting === c.id && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  start(c.id);
                }}
                style={{ marginTop: "1rem" }}
              >
                <label>
                  Type <code>{c.id}</code> to start it for real. Its recipients are snapshotted now; batches go out
                  from {when(c.start_at)} while sending is {overview?.sending_enabled ? "on" : "off"}.
                  <input value={confirmId} onChange={(e) => setConfirmId(e.target.value)} autoFocus />
                </label>
                <div role="group" style={{ width: "auto" }}>
                  <button type="submit" disabled={busy || confirmId.trim() !== c.id}>
                    Start {c.id}
                  </button>
                  <button type="button" className="secondary outline" onClick={() => setStarting(null)}>
                    Never mind
                  </button>
                </div>
              </form>
            )}
            {dryRun?.campaign_id === c.id && (
              <div style={{ marginTop: "1rem" }}>
                <p>
                  <strong>Dry run: nothing was sent.</strong>{" "}
                  {Object.entries(dryRun.raw_counts)
                    .map(([source, n]) => `${n} from ${source}`)
                    .join(", ")}
                  ; {dryRun.duplicates_removed} duplicates removed; left out {dryRun.skipped_counts.invalid ?? 0}{" "}
                  invalid, {dryRun.skipped_counts.owner ?? 0} owner, {dryRun.skipped_counts.suppressed ?? 0}{" "}
                  suppressed. <strong>{dryRun.recipient_count} would receive it</strong>, in {dryRun.batches} batch(es).
                </p>
                <details>
                  <summary>Recipients ({dryRun.recipients.length})</summary>
                  <pre style={{ maxHeight: "16rem", overflow: "auto" }}>{dryRun.recipients.join("\n")}</pre>
                </details>
                {dryRun.skipped.length > 0 && (
                  <details>
                    <summary>Left out ({dryRun.skipped.length})</summary>
                    <pre style={{ maxHeight: "16rem", overflow: "auto" }}>
                      {dryRun.skipped.map((s) => `${s.email}  (${s.reason})`).join("\n")}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </section>
        ))}
      </article>

      {draft && overview && (
        <article>
          <h2>{editing ? `Edit ${editing}` : "New campaign"}</h2>
          <form onSubmit={save}>
            <div className="grid">
              <label>
                Id
                <input
                  required
                  value={draft.id}
                  onChange={field("id")}
                  readOnly={editing !== null}
                  pattern="[a-z0-9][a-z0-9\-]{0,63}"
                  placeholder="digitizing-announcement"
                />
                <small>Lowercase letters, digits and dashes. Goes into links as ?campaign=, so it's public.</small>
              </label>
              <label>
                Name
                <input value={draft.name} onChange={field("name")} placeholder="Defaults to the id" />
              </label>
            </div>
            <label>
              Subject
              <input required value={draft.subject} onChange={field("subject")} />
            </label>
            <label>
              Body (plain text)
              <textarea rows={10} required value={draft.body_text} onChange={field("body_text")} />
              <small>
                Links to embroiderytext.com get ?source=email&campaign=… added. The unsubscribe link and postal address
                are added below it.
              </small>
            </label>
            <label>
              Body (HTML, optional)
              <textarea rows={6} value={draft.body_html} onChange={field("body_html")} />
              <small>Leave empty to send the plain text, converted.</small>
            </label>
            <div className="grid">
              <label>
                From
                <input value={draft.from} onChange={field("from")} placeholder="The support address" />
              </label>
              <label>
                Reply-to
                <input value={draft.reply_to} onChange={field("reply_to")} placeholder="The support address" />
              </label>
            </div>
            <fieldset>
              <legend>Recipients</legend>
              {Object.entries(overview.sources).map(([source, description]) => (
                <label key={source}>
                  <input
                    type="checkbox"
                    checked={draft.sources.includes(source)}
                    onChange={(e) =>
                      setDraft((d) =>
                        d
                          ? {
                              ...d,
                              sources: e.target.checked
                                ? [...d.sources, source]
                                : d.sources.filter((s) => s !== source),
                            }
                          : d,
                      )
                    }
                  />
                  {description}
                </label>
              ))}
            </fieldset>
            <div className="grid">
              <label>
                Start
                <input type="datetime-local" required value={draft.start_at} onChange={field("start_at")} />
                <small>Your local time.</small>
              </label>
              <label>
                Batch size
                <input
                  type="number"
                  min={1}
                  max={overview.max_batch_size}
                  step={1}
                  required
                  value={draft.batch_size}
                  onChange={field("batch_size")}
                />
              </label>
              <label>
                Minutes between batches
                <input
                  type="number"
                  min={1}
                  step="any"
                  required
                  value={draft.batch_interval_minutes}
                  onChange={field("batch_interval_minutes")}
                />
              </label>
            </div>
            <label>
              <input
                type="checkbox"
                role="switch"
                checked={draft.dry_run}
                onChange={(e) => setDraft((d) => (d ? { ...d, dry_run: e.target.checked } : d))}
              />
              Dry-run mode: can be dry-run and test-sent, but not started
            </label>
            <div role="group" style={{ width: "auto" }}>
              <button type="submit" disabled={busy || draft.sources.length === 0} aria-busy={busy}>
                Save draft
              </button>
              <button
                type="button"
                className="secondary outline"
                onClick={() => {
                  setDraft(null);
                  setEditing(null);
                }}
              >
                Close
              </button>
            </div>
          </form>
        </article>
      )}

      <article>
        <h2>Suppression list</h2>
        <p>
          <small>
            No campaign emails these addresses. Transactional email (a design someone asked for, a quote confirmation)
            is unaffected.
          </small>
        </p>
        <form onSubmit={suppress} role="group">
          <input
            type="email"
            required
            placeholder="someone@example.com"
            value={suppressInput}
            onChange={(e) => setSuppressInput(e.target.value)}
          />
          <button type="submit" disabled={busy}>
            Suppress
          </button>
        </form>
        {suppressions === null && <p aria-busy="true">Loading…</p>}
        {suppressions?.length === 0 && <p>Nobody is suppressed.</p>}
        {suppressions && suppressions.length > 0 && (
          <div className="overflow-auto">
            <table>
              <thead>
                <tr>
                  <th scope="col">Address</th>
                  <th scope="col">Why</th>
                  <th scope="col">From</th>
                  <th scope="col">When</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {suppressions.map((s) => (
                  <tr key={s.email}>
                    <td>{s.email}</td>
                    <td>{s.reason}</td>
                    <td>{s.source ?? "—"}</td>
                    <td>{when(s.created_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="secondary outline"
                        disabled={busy}
                        onClick={() => unsuppress(s.email)}
                        style={{ margin: 0, padding: "0.2rem 0.6rem" }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {suppressions.length === 500 && <small>Showing the latest 500.</small>}
          </div>
        )}
      </article>
    </>
  );
}
